import { TrackerEntityKind } from "@/enum/tracker.enum";

/**
 * What each kind of tracker source demands, and what to call it (PREC-3417).
 *
 * The Profile Tracker watches a *specific* entity over time, so it can only accept a row it
 * can address precisely. Keeping that rule here — as data, with no React around it — means
 * the Sources step, the mapping modal and the source cards all read it from one place, and it
 * can be tested without rendering anything.
 *
 * It deliberately mirrors `REQUIRED_IDENTIFIER_COMBINATIONS` in the API's
 * `tracker-source-rows.util.ts`: the server refuses an upload whose mapping does not satisfy
 * it, so a UI that disagreed would either block a valid file or promise one the API rejects.
 */

/** Mapping target ids, as the API's field configuration names them. */
export const PERSON_IDENTIFIER = "linkedinUrl";
export const COMPANY_WEBSITE = "websiteUrl";
export const COMPANY_LINKEDIN = "companyLinkedinUrl";

/** The mapping value meaning "keep this column as free-form data". */
export const CUSTOM_FIELD = "customField";

/**
 * Any one of these fields satisfies the source. Companies accept either identifier because
 * the tracker resolves an organization by LinkedIn URL first and falls back to the website —
 * demanding one specific column would reject files that carry the other and resolve fine.
 */
const ACCEPTED_IDENTIFIERS: Record<TrackerEntityKind, string[]> = {
	[TrackerEntityKind.PERSON]: [PERSON_IDENTIFIER],
	[TrackerEntityKind.ORGANIZATION]: [COMPANY_WEBSITE, COMPANY_LINKEDIN],
};

export const acceptedIdentifiers = (entityKind: TrackerEntityKind): string[] =>
	ACCEPTED_IDENTIFIERS[entityKind] ?? ACCEPTED_IDENTIFIERS[TrackerEntityKind.PERSON];

/** Whether a mapped field is the identifier that makes the source valid. */
export const isIdentifierField = (entityKind: TrackerEntityKind, assignedField: string): boolean =>
	acceptedIdentifiers(entityKind).includes(assignedField);

/**
 * Whether the current mapping lets the source be saved. This is the "force the user" gate:
 * the confirm button stays disabled until the identifier is mapped, so the user is told what
 * is missing instead of discovering it in a rejected upload.
 */
export const mappingSatisfiesEntityKind = (
	configuration: { assignedField: string }[] | undefined,
	entityKind: TrackerEntityKind
): boolean =>
	(configuration ?? []).some((mapping) => isIdentifierField(entityKind, mapping.assignedField));

/** i18n key suffix for the kind, so callers read `sources.required.${suffix}`. */
export const entityKindKey = (entityKind: TrackerEntityKind): "people" | "companies" =>
	entityKind === TrackerEntityKind.ORGANIZATION ? "companies" : "people";

export interface MappingOption {
	title: string;
	id: string;
	/** Header names that should auto-select this target. */
	compares: string[];
}

const PEOPLE_OPTIONS: MappingOption[] = [
	{
		title: "Personal LinkedIn",
		id: PERSON_IDENTIFIER,
		compares: ["linkedinUrl", "Personal Linkedin", "LinkedIn URL", "Linkedin", "Profile URL"],
	},
	{ title: "Full Name", id: "name", compares: ["name", "Name", "Full Name"] },
	{ title: "Firstname", id: "firstName", compares: ["firstName", "Firstname", "First Name"] },
	{ title: "Lastname", id: "lastName", compares: ["lastName", "Lastname", "Last Name"] },
	{ title: "Job Title", id: "title", compares: ["title", "Job Title", "Position"] },
	{
		title: "Company Name",
		id: "companyName",
		compares: ["companyName", "Company Name", "Company"],
	},
	{
		title: "Company LinkedIn",
		id: COMPANY_LINKEDIN,
		compares: ["companyLinkedinUrl", "Company Linkedin", "Company LinkedIn URL"],
	},
	{
		title: "Company Website",
		id: COMPANY_WEBSITE,
		compares: ["websiteUrl", "Website", "Company Website", "Domain"],
	},
];

const COMPANY_OPTIONS: MappingOption[] = [
	{
		title: "Company Website",
		id: COMPANY_WEBSITE,
		compares: ["websiteUrl", "Website", "Company Website", "Domain"],
	},
	{
		title: "Company LinkedIn",
		id: COMPANY_LINKEDIN,
		compares: ["companyLinkedinUrl", "Company Linkedin", "Company LinkedIn URL"],
	},
	{
		title: "Company Name",
		id: "name",
		compares: ["name", "companyName", "Company Name", "Company"],
	},
	{
		title: "Holding LinkedIn",
		id: "holdingLinkedinUrl",
		compares: ["holdingLinkedinUrl", "Holding Linkedin", "Parent Company Linkedin"],
	},
];

/** The fields a source of this kind can map its columns onto, identifier first. */
export const mappingOptions = (entityKind: TrackerEntityKind): MappingOption[] =>
	entityKind === TrackerEntityKind.ORGANIZATION ? COMPANY_OPTIONS : PEOPLE_OPTIONS;

/**
 * The best guess for a column, by header name. Falls back to nothing rather than to a wrong
 * field: an unmapped column is visible and fixable, a mis-mapped one is neither.
 */
export const suggestMappingForHeader = (header: string, entityKind: TrackerEntityKind): string => {
	const normalized = header.trim().toLowerCase();

	return (
		mappingOptions(entityKind).find((option) =>
			option.compares.some((candidate) => candidate.toLowerCase() === normalized)
		)?.id ?? ""
	);
};
